import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { timeout, enqueueTask, dropTask, restartableTask } from 'ember-concurrency';
import PowerSelect, { type PowerSelectArgs, type Select } from 'ember-power-select/components/power-select';
import { localCopy } from 'tracked-toolbox';

import ScrollAwareOptions from './paginated-select/scroll-aware-options';

type Option<T> = T & { isEqual?: (b: T) => boolean };

interface PaginatedSelectSignature<T> {
  Args: PowerSelectArgs & {
    /**
     * Return an array or promised array of options for the page/search params
     */
    fetchOptions: (page: number, term: string, select: Select) => Option<T>[] | Promise<Option<T>[]>;
    /**
     * Fires after the initial options are loaded
     */
    onLoaded?: (options: T[], select: Select) => void;
    /**
     * How to determine if two options are equal
     */
    optionsAreEqual?: (a: T, b: T) => boolean;
    /**
     * Toggle infinite-scroll pagination on or off
     * @defaultValue false
     */
    paginatedEnabled?: boolean;
    /**
     * Amount to scroll before loading more data
     * @defaultValue 0.9
     */
    scrollThresholdPercent?: number;
    /**
     * Amount of time to wait during an active search before fetching data
     * @defaultValue 250
     */
    searchThresholdMs?: number;
  };
  Blocks: {
    default: [T];
  };
}

export default class PaginatedSelect<T> extends Component<PaginatedSelectSignature<T>> {
  @localCopy('args.paginateEnabled', false) declare paginateEnabled: boolean;
  @localCopy('args.scrollThresholdPercent', 0.9) declare scrollThresholdPercent: number;
  @localCopy('args.searchThresholdMs', 250) declare searchThresholdMs: number;

  /*
   * private attributes
   */
  @tracked _api: Select | null = null;
  @tracked _optionsPage = 1;
  @tracked _hasMoreData = true;
  _defaultOptions: Option<T>[] = [];

  @tracked options: Option<T>[] = [];

  // This solves a problem where TS/Glint don't handle passing template types using the `component` helper, with this error:
  // error TS2589: Type instantiation is excessively deep and possibly infinite.
  // Putting this on the class (and passing it to `component`, instead of the imported component) "fixes" the issue.
  ScrollAwareOptions = ScrollAwareOptions<T>;

  constructor(owner: unknown, args: PaginatedSelectSignature<T>['Args']) {
    super(owner, args);

    void this._fetchDefaultOptions.perform();
  }

  @action
  registerAPI(publicAPI: Select): void {
    this._api = publicAPI;
  }

  @action
  search(term: string, _select: Select): Promise<Option<T>[]> {
    /*
     * The types for PowerSelect expect that the `@search` function will return a list of options.
     * However, it works to return nothing from `@search`, in which case, the PowerSelect will use `@options`.
     * Because we can't just get a `@ts-expect-error` directive to work inside the <template></template> here,
     * intentionally mis-typing this method and ignoring the incorrect return value is the simplest way forward.
     */
    // @ts-expect-error - See the comment above
    return this._searchOptions.perform(term);
  }

  @action
  onInput(term: string, _select: Select, _event: Event): void {
    /**
     * Resets the page to 1 on each search.
     */
    this._optionsPage = 1;
    this._hasMoreData = true;
    if (!term) {
      /*
       * PowerSelect does not fire the "search" action if the term is empty,
       * so we need to manually reset the options to its original data.
       */
      this.options = this._defaultOptions;
    }
  }

  @action
  _reportScroll(scrolledPercent: number): void {
    void this._scrollOptions.perform(scrolledPercent);
  }

  /**
   * Get first page.
   */
  _fetchDefaultOptions = dropTask(async () => {
    const options = await this._fetchOptions.perform();
    this.options = options;
    this._defaultOptions = options;
    this.args.onLoaded?.(options, this._api!);
  });

  /**
   * Enqueue all requests to fetchOptions.
   */
  _fetchOptions = enqueueTask(async (page: number = 1, term: string = '') => {
    const options = (await this.args.fetchOptions?.(page, term, this._api!)) ?? [];
    const optionsAreEqual = this.args.optionsAreEqual;
    if (!optionsAreEqual) {
      return options;
    }
    return (options || []).map((a) => {
      a.isEqual = (b) => optionsAreEqual(a, b); // used interally by power-select
      return a;
    });
  });

  /**
   * A debounced search.
   */
  _searchOptions = restartableTask(async (term: string = '') => {
    await timeout(this.searchThresholdMs);
    const options = await this._fetchOptions.perform(this._optionsPage, term);
    this.options = options;
  });

  _scrollOptions = dropTask(async (scrolledPercent: number) => {
    /**
     * Perform pagination only once when the scroll is over the threshold, and only when applicable.
     */
    if (!this.paginateEnabled || !this._hasMoreData || scrolledPercent < this.scrollThresholdPercent) {
      return;
    }
    this._optionsPage = this._optionsPage + 1;

    const options = await this._fetchOptions.perform(this._optionsPage, this._api!.searchText);
    this._hasMoreData = options.length > 0;
    this.options = this.options.concat(options);
  });

  <template>
    <PowerSelect
      @afterOptionsComponent={{@afterOptionsComponent}}
      @allowClear={{@allowClear}}
      @animationEnabled={{@animationEnabled}}
      @ariaDescribedBy={{@ariaDescribedBy}}
      @ariaInvalid={{@ariaInvalid}}
      @ariaLabel={{@ariaLabel}}
      @ariaLabelledBy={{@ariaLabelledBy}}
      @beforeOptionsComponent={{@beforeOptionsComponent}}
      @buildSelection={{@buildSelection}}
      @calculatePosition={{@calculatePosition}}
      @closeOnSelect={{@closeOnSelect}}
      @defaultHighlighted={{@defaultHighlighted}}
      @destination={{@destination}}
      @disabled={{@disabled}}
      @dropdownClass={{@dropdownClass}}
      @extra={{@extra}}
      @groupComponent={{@groupComponent}}
      @highlightOnHover={{@highlightOnHover}}
      @horizontalPosition={{@horizontalPosition}}
      @initiallyOpened={{@initiallyOpened}}
      @loadingMessage={{@loadingMessage}}
      @eventType={{@eventType}}
      @matcher={{@matcher}}
      @matchTriggerWidth={{@matchTriggerWidth}}
      @noMatchesMessage={{@noMatchesMessage}}
      @onBlur={{@onBlur}}
      @onChange={{@onChange}}
      @onClose={{@onClose}}
      @onFocus={{@onFocus}}
      @onInput={{this.onInput}}
      @onKeydown={{@onKeydown}}
      @onOpen={{@onOpen}}
      @registerAPI={{this.registerAPI}}
      @options={{this.options}}
      @optionsComponent={{component this.ScrollAwareOptions onScroll=this._reportScroll}}
      @placeholder={{@placeholder}}
      @placeholderComponent={{@placeholderComponent}}
      @preventScroll={{@preventScroll}}
      @renderInPlace={{@renderInPlace}}
      @scrollTo={{@scrollTo}}
      @search={{this.search}}
      @searchEnabled={{@searchEnabled}}
      @searchField={{@searchField}}
      @searchMessage={{@searchMessage}}
      @searchPlaceholder={{@searchPlaceholder}}
      @selected={{@selected}}
      @selectedItemComponent={{@selectedItemComponent}}
      @tabindex={{@tabindex}}
      @triggerClass={{@triggerClass}}
      @triggerComponent={{@triggerComponent}}
      @triggerId={{@triggerId}}
      @triggerRole={{@triggerRole}}
      @verticalPosition={{@verticalPosition}}
      as |option|
    >
      {{yield option}}
    </PowerSelect>
  </template>
}
